/* LM Community Forum — storefront client.
 *
 * Renders inside the Shopify theme and talks to the community API. Nothing here
 * is trusted by the server: the only thing that establishes who the member is
 * is the signed identity blob Liquid rendered, which is exchanged once for a
 * session token.
 *
 * All member-supplied text reaches the DOM through textContent, never innerHTML,
 * so a post can never inject markup into the storefront.
 */
(function () {
  'use strict';

  var root = document.getElementById('lm-forum');
  if (!root) return;

  var API = (root.dataset.api || '').replace(/\/$/, '');
  var LOGIN_URL = root.dataset.loginUrl || '/account/login';
  var WHATSAPP = root.dataset.whatsapp || '';

  var view = document.getElementById('lmf-view');
  var authbar = document.getElementById('lmf-authbar');
  var flash = document.getElementById('lmf-flash');
  if (!view || !API || !window.LMAuth) return;

  // Sessions, the Liquid identity blob and Google all live in lm-auth.js, which
  // the community feed shares. This file owns only the forum's own UI state.
  var LMAuth = window.LMAuth.init({
    api: API,
    googleClientId: root.dataset.googleClientId || '',
  });

  var state = { boards: null };

  // --- tiny DOM helpers ---------------------------------------------------

  function el(tag, opts, children) {
    var node = document.createElement(tag);
    opts = opts || {};
    if (opts.className) node.className = opts.className;
    if (opts.text != null) node.textContent = String(opts.text);
    if (opts.html != null) node.innerHTML = opts.html; // only ever called with literals
    if (opts.attrs) {
      for (var key in opts.attrs) {
        if (opts.attrs[key] != null) node.setAttribute(key, opts.attrs[key]);
      }
    }
    if (opts.on) {
      for (var evt in opts.on) node.addEventListener(evt, opts.on[evt]);
    }
    (children || []).forEach(function (child) {
      if (child) node.appendChild(child);
    });
    return node;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function showFlash(message, kind) {
    if (!flash) return;
    flash.className = 'lmf-flash lmf-flash--' + (kind === 'ok' ? 'ok' : 'err');
    flash.textContent = message;
    flash.hidden = false;
    if (kind === 'ok') {
      window.setTimeout(function () {
        flash.hidden = true;
      }, 4000);
    }
  }

  function hideFlash() {
    if (flash) flash.hidden = true;
  }

  function timeAgo(iso) {
    var then = new Date(iso).getTime();
    if (!then) return '';
    var seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (seconds < 60) return 'just now';
    var minutes = Math.floor(seconds / 60);
    if (minutes < 60) return minutes + 'm ago';
    var hours = Math.floor(minutes / 60);
    if (hours < 24) return hours + 'h ago';
    var days = Math.floor(hours / 24);
    if (days < 30) return days + 'd ago';
    return new Date(iso).toLocaleDateString();
  }

  function initials(name) {
    return (name || '?')
      .split(/\s+/)
      .slice(0, 2)
      .map(function (part) {
        return part.charAt(0).toUpperCase();
      })
      .join('');
  }

  function avatar(author, className) {
    var node = el('span', { className: 'lmf-av ' + (className || '') });
    if (author && author.avatarUrl) {
      node.appendChild(
        el('img', { attrs: { src: author.avatarUrl, alt: '', loading: 'lazy' } }),
      );
    } else {
      node.textContent = initials(author && author.displayName);
    }
    return node;
  }

  function authorLine(author) {
    var wrap = el('span', { className: 'lmf-who' }, [avatar(author)]);
    var name = el('span', { className: 'lmf-name', text: author.displayName });
    wrap.appendChild(name);
    if (author.isTeam) wrap.appendChild(el('span', { className: 'lmf-team', text: 'LM Team' }));
    return wrap;
  }

  function badge(text, kind) {
    return el('span', { className: 'lmf-badge lmf-badge--' + kind, text: text });
  }

  // --- session -----------------------------------------------------------

  function signOut() {
    LMAuth.signOut();
    renderAuthBar();
    route();
  }

  LMAuth.onChange(function () {
    // lm-auth clears the session on any 401, including one raised mid-render.
    renderAuthBar();
  });

  // --- auth bar -----------------------------------------------------------

  function renderAuthBar() {
    if (!authbar) return;
    clear(authbar);
    authbar.hidden = false;

    if (LMAuth.me()) {
      authbar.appendChild(authorLine(LMAuth.me()));

      var actions = el('div', { className: 'lmf-authbar-actions' });

      if (!LMAuth.me().canPost) {
        var note = el('span', {
          className: 'lmf-authnote',
          text: LMAuth.me().mutedUntil
            ? 'Posting paused until ' + new Date(LMAuth.me().mutedUntil).toLocaleDateString() + '.'
            : 'Posting is paused on your account.',
        });
        actions.appendChild(note);
        if (WHATSAPP) {
          actions.appendChild(
            el('a', {
              className: 'lmf-btn lmf-btn--sm',
              text: 'Message the team',
              attrs: {
                href: 'https://wa.me/' + WHATSAPP,
                target: '_blank',
                rel: 'noopener',
              },
            }),
          );
        }
      }

      // Offer to connect whichever sign-in they have not linked yet, so both
      // routes land on this same profile next time.
      var linked = LMAuth.me().linkedProviders || [];
      if (LMAuth.hasGoogle() && linked.indexOf('google') === -1) {
        var linkSlot = el('div', { attrs: { id: 'lmf-google-link' } });
        actions.appendChild(
          el('span', { className: 'lmf-authnote', text: 'Also sign in with Google:' }),
        );
        actions.appendChild(linkSlot);
        renderGoogleButton(linkSlot, 'link');
      }

      actions.appendChild(
        el('button', {
          className: 'lmf-btn lmf-btn--sm lmf-btn--ghost',
          text: 'Sign out',
          on: { click: function () { signOut(); } },
        }),
      );

      authbar.appendChild(actions);
      return;
    }

    authbar.appendChild(
      el('span', {
        className: 'lmf-authnote',
        text: 'Reading is open to everyone. Sign in to post a thread or reply.',
      }),
    );

    var guest = el('div', { className: 'lmf-authbar-actions' }, [
      el('a', {
        className: 'lmf-btn lmf-btn--primary lmf-btn--sm',
        text: 'Sign in with your store account',
        attrs: { href: LOGIN_URL + '?return_url=' + encodeURIComponent(window.location.pathname) },
      }),
    ]);

    if (LMAuth.hasGoogle()) {
      var slot = el('div', { attrs: { id: 'lmf-google-signin' } });
      guest.appendChild(slot);
      renderGoogleButton(slot, 'signin');
    }

    authbar.appendChild(guest);
  }

  // --- Google sign-in -----------------------------------------------------

  /** Delegates to lm-auth.js; this file only decides what to say afterwards. */
  function renderGoogleButton(slot, mode) {
    LMAuth.renderGoogleButton(slot, mode, function (error, result) {
      if (error) return showFlash(error.message, 'err');
      if (result.linked) {
        showFlash('Google sign-in connected to your profile.', 'ok');
      } else if (result.merged) {
        showFlash('Welcome back — this is the same profile as your store account.', 'ok');
      }
      renderAuthBar();
      route();
    });
  }

  // --- routing ------------------------------------------------------------

  function go(hash) {
    if (window.location.hash === hash) route();
    else window.location.hash = hash;
  }

  function route() {
    hideFlash();
    var hash = window.location.hash.replace(/^#/, '');
    var parts = hash.split('/').filter(Boolean);

    if (parts[0] === 'b' && parts[1] && parts[2] === 'new') return viewComposeThread(parts[1]);
    if (parts[0] === 'b' && parts[1]) return viewBoard(parts[1], parseInt(parts[2], 10) || 1);
    if (parts[0] === 't' && parts[1]) return viewThread(parts[1]);
    return viewBoards();
  }

  function loading(message) {
    clear(view);
    view.appendChild(el('div', { className: 'lmf-loading', text: message || 'Loading…' }));
  }

  function failed(error) {
    clear(view);
    view.appendChild(
      el('div', { className: 'lmf-notice lmf-notice--warn', text: error.message }),
    );
  }

  function crumbs(trail) {
    var wrap = el('div', { className: 'lmf-crumbs' });
    trail.forEach(function (entry, index) {
      if (index > 0) wrap.appendChild(el('span', { className: 'lmf-dot', text: '›' }));
      if (entry.hash) {
        wrap.appendChild(
          el('button', {
            text: entry.label,
            attrs: { type: 'button' },
            on: { click: function () { go(entry.hash); } },
          }),
        );
      } else {
        wrap.appendChild(el('span', { text: entry.label }));
      }
    });
    return wrap;
  }

  // --- view: board index --------------------------------------------------

  function viewBoards() {
    loading('Loading boards…');
    LMAuth.api('/api/boards')
      .then(function (payload) {
        state.boards = payload.boards;
        clear(view);

        var grid = el('div', { className: 'lmf-boards' });
        payload.boards.forEach(function (board) {
          grid.appendChild(
            el(
              'button',
              {
                className: 'lmf-board',
                attrs: { type: 'button' },
                on: { click: function () { go('#/b/' + board.slug); } },
              },
              [
                el('div', { className: 'lmf-board-name', text: board.name }),
                el('div', { className: 'lmf-board-desc', text: board.description }),
                el('div', {
                  className: 'lmf-board-count',
                  text:
                    board.threadCount === 1 ? '1 thread' : board.threadCount + ' threads',
                }),
              ],
            ),
          );
        });
        view.appendChild(grid);
      })
      .catch(failed);
  }

  // --- view: one board ----------------------------------------------------

  function viewBoard(slug, page) {
    loading('Loading threads…');
    LMAuth.api('/api/boards/' + encodeURIComponent(slug) + '/threads?page=' + page)
      .then(function (payload) {
        clear(view);
        view.appendChild(crumbs([{ label: 'Forum', hash: '#/' }, { label: payload.board.name }]));

        var head = el('div', { className: 'lmf-toolbar' }, [
          el('div', {}, [
            el('h2', { text: payload.board.name }),
            el('p', { text: payload.board.description }),
          ]),
        ]);
        var actions = el('div', { className: 'lmf-toolbar-actions' });
        if (LMAuth.me() && LMAuth.me().canPost) {
          actions.appendChild(
            el('button', {
              className: 'lmf-btn lmf-btn--primary',
              text: '+ Start a thread',
              attrs: { type: 'button' },
              on: { click: function () { go('#/b/' + slug + '/new'); } },
            }),
          );
        }
        head.appendChild(actions);
        view.appendChild(head);

        if (payload.threads.length === 0) {
          view.appendChild(
            el('div', { className: 'lmf-empty' }, [
              el('div', { className: 'lmf-empty-emoji', text: '🫧' }),
              el('p', { text: 'No threads here yet. Be the first to post.' }),
            ]),
          );
        } else {
          var list = el('div', { className: 'lmf-threads' });
          payload.threads.forEach(function (thread) {
            list.appendChild(threadRow(thread));
          });
          view.appendChild(list);
        }

        if (payload.total > payload.perPage) {
          view.appendChild(pager(slug, payload));
        }
      })
      .catch(failed);
  }

  function threadRow(thread) {
    var className = 'lmf-thread';
    if (thread.isPinned) className += ' lmf-thread--pinned';

    var top = el('div', { className: 'lmf-thread-top' });
    if (thread.isPinned) top.appendChild(badge('Pinned', 'pin'));
    if (thread.isAnswered) top.appendChild(badge('Answered', 'ans'));
    if (thread.isLocked) top.appendChild(badge('Locked', 'lock'));
    if (thread.photoCount > 0) {
      top.appendChild(badge('📷 ' + thread.photoCount, 'photo'));
    }
    top.appendChild(el('span', { className: 'lmf-thread-title', text: thread.title }));

    var meta = el('div', { className: 'lmf-thread-meta' }, [
      authorLine(thread.author),
      el('span', { className: 'lmf-dot', text: '•' }),
      el('span', {
        text: thread.replyCount === 1 ? '1 reply' : thread.replyCount + ' replies',
      }),
      el('span', { className: 'lmf-dot', text: '•' }),
      el('span', { text: timeAgo(thread.lastReplyAt) }),
    ]);

    return el(
      'button',
      {
        className: className,
        attrs: { type: 'button' },
        on: { click: function () { go('#/t/' + thread.id); } },
      },
      [top, el('div', { className: 'lmf-thread-ex', text: thread.excerpt }), meta],
    );
  }

  function pager(slug, payload) {
    var totalPages = Math.ceil(payload.total / payload.perPage);
    var wrap = el('div', { className: 'lmf-pager' });

    wrap.appendChild(
      el('button', {
        className: 'lmf-btn lmf-btn--sm',
        text: '← Newer',
        attrs: { type: 'button', disabled: payload.page <= 1 ? 'disabled' : null },
        on: { click: function () { go('#/b/' + slug + '/' + (payload.page - 1)); } },
      }),
    );
    wrap.appendChild(el('span', { text: 'Page ' + payload.page + ' of ' + totalPages }));
    wrap.appendChild(
      el('button', {
        className: 'lmf-btn lmf-btn--sm',
        text: 'Older →',
        attrs: { type: 'button', disabled: payload.hasMore ? null : 'disabled' },
        on: { click: function () { go('#/b/' + slug + '/' + (payload.page + 1)); } },
      }),
    );
    return wrap;
  }

  // --- view: thread detail ------------------------------------------------

  function viewThread(id) {
    loading('Loading thread…');
    LMAuth.api('/api/threads/' + encodeURIComponent(id))
      .then(function (payload) {
        var thread = payload.thread;
        clear(view);

        view.appendChild(
          crumbs([
            { label: 'Forum', hash: '#/' },
            { label: boardName(thread.boardSlug), hash: '#/b/' + thread.boardSlug },
            { label: 'Thread' },
          ]),
        );

        view.appendChild(openingPost(thread));

        thread.replies.forEach(function (reply) {
          view.appendChild(replyPost(thread, reply));
        });

        if (thread.isLocked) {
          view.appendChild(
            el('div', {
              className: 'lmf-notice lmf-notice--muted',
              text: '🔒 This thread is locked. No new replies can be added.',
            }),
          );
        } else if (LMAuth.me() && LMAuth.me().canPost) {
          view.appendChild(replyComposer(thread));
        } else if (!LMAuth.me()) {
          view.appendChild(
            el('div', {
              className: 'lmf-notice lmf-notice--muted',
              text: 'Sign in above to join this conversation.',
            }),
          );
        }
      })
      .catch(failed);
  }

  function boardName(slug) {
    if (state.boards) {
      for (var i = 0; i < state.boards.length; i += 1) {
        if (state.boards[i].slug === slug) return state.boards[i].name;
      }
    }
    return slug;
  }

  function postHead(thread, author, createdAt, extraActions) {
    var head = el('div', { className: 'lmf-post-head' }, [
      authorLine(author),
      el('span', { className: 'lmf-post-when', text: timeAgo(createdAt) }),
    ]);
    if (extraActions) head.appendChild(extraActions);
    return head;
  }

  function photoStrip(photos) {
    if (!photos || photos.length === 0) return null;
    var strip = el('div', { className: 'lmf-photos' });
    photos.forEach(function (photo) {
      strip.appendChild(
        el('img', {
          attrs: { src: photo.url, alt: 'Photo attached to this post', loading: 'lazy' },
          on: { click: function () { lightbox(photo.url); } },
        }),
      );
    });
    return strip;
  }

  function openingPost(thread) {
    var className = 'lmf-post lmf-post--op';
    if (thread.isRemoved) className += ' lmf-post--removed';

    var badges = el('div', { className: 'lmf-thread-top' });
    if (thread.isPinned) badges.appendChild(badge('Pinned', 'pin'));
    if (thread.isAnswered) badges.appendChild(badge('Answered', 'ans'));
    if (thread.isLocked) badges.appendChild(badge('Locked', 'lock'));
    if (thread.isRemoved) badges.appendChild(badge('Removed', 'rm'));

    return el('article', { className: className }, [
      badges,
      el('h2', { className: 'lmf-post-title', text: thread.title }),
      postHead(thread, thread.author, thread.createdAt, threadModMenu(thread)),
      el('div', { className: 'lmf-post-body', text: thread.body }),
      photoStrip(thread.photos),
    ]);
  }

  function replyPost(thread, reply) {
    var className = 'lmf-post';
    if (reply.isAnswer) className += ' lmf-post--answer';
    if (reply.isRemoved) className += ' lmf-post--removed';

    var badges = null;
    if (reply.isAnswer) {
      badges = el('div', { className: 'lmf-thread-top' }, [badge('✓ Answer', 'ans')]);
    }

    return el('article', { className: className }, [
      badges,
      postHead(thread, reply.author, reply.createdAt, replyModMenu(thread, reply)),
      el('div', { className: 'lmf-post-body', text: reply.body }),
      photoStrip(reply.photos),
    ]);
  }

  // --- moderation ---------------------------------------------------------

  function modButton(label, handler) {
    return el('button', {
      className: 'lmf-btn lmf-btn--sm lmf-btn--ghost',
      text: label,
      attrs: { type: 'button' },
      on: { click: handler },
    });
  }

  function patchThread(thread, action, extra) {
    var body = { action: action };
    if (extra) for (var key in extra) body[key] = extra[key];
    return LMAuth.api('/api/threads/' + thread.id, { method: 'PATCH', body: body })
      .then(function () {
        viewThread(thread.id);
      })
      .catch(function (error) {
        showFlash(error.message, 'err');
      });
  }

  function threadModMenu(thread) {
    if (!LMAuth.me()) return null;
    var team = LMAuth.me().isTeam;
    var isAuthor = thread.author && thread.author.id === LMAuth.me().id;
    if (!team && !isAuthor) return null;

    var menu = el('div', { className: 'lmf-post-actions' });

    // The thread's own author may resolve their question; everything else is
    // the LM team's (build reference §5.14, §5.15).
    if (thread.isAnswered) {
      menu.appendChild(
        modButton('Unmark answered', function () {
          patchThread(thread, 'unmark_answered');
        }),
      );
    } else {
      menu.appendChild(
        modButton('Mark answered', function () {
          patchThread(thread, 'mark_answered');
        }),
      );
    }

    if (!team) return menu;

    menu.appendChild(
      modButton(thread.isPinned ? 'Unpin' : 'Pin', function () {
        patchThread(thread, thread.isPinned ? 'unpin' : 'pin');
      }),
    );
    menu.appendChild(
      modButton(thread.isLocked ? 'Unlock' : 'Lock', function () {
        patchThread(thread, thread.isLocked ? 'unlock' : 'lock');
      }),
    );
    menu.appendChild(
      modButton(thread.isRemoved ? 'Restore' : 'Remove', function () {
        if (thread.isRemoved) return patchThread(thread, 'restore');
        // Confirm the impactful actions only — pinning and locking are cheap
        // to undo, removal changes what every member sees.
        if (window.confirm('Remove this thread? Members will see "[removed by moderator]".')) {
          patchThread(thread, 'remove');
        }
      }),
    );

    return menu;
  }

  function replyModMenu(thread, reply) {
    if (!LMAuth.me()) return null;
    var team = LMAuth.me().isTeam;
    var isThreadAuthor = thread.author && thread.author.id === LMAuth.me().id;
    if (!team && !isThreadAuthor) return null;

    var menu = el('div', { className: 'lmf-post-actions' });

    if (!reply.isRemoved && !reply.isAnswer) {
      menu.appendChild(
        modButton('This answered it', function () {
          patchThread(thread, 'mark_answered', { replyId: reply.id });
        }),
      );
    }

    if (team) {
      menu.appendChild(
        modButton(reply.isRemoved ? 'Restore' : 'Remove', function () {
          var action = reply.isRemoved ? 'restore' : 'remove';
          if (action === 'remove' && !window.confirm('Remove this reply?')) return;
          LMAuth.api('/api/replies/' + reply.id, { method: 'PATCH', body: { action: action } })
            .then(function () {
              viewThread(thread.id);
            })
            .catch(function (error) {
              showFlash(error.message, 'err');
            });
        }),
      );
    }

    return menu.childNodes.length ? menu : null;
  }

  // --- photo picker -------------------------------------------------------

  /**
   * Uploads immediately on selection and holds the signed token the server
   * returns. The post itself submits tokens, not URLs — the server refuses
   * anything it did not sign.
   */
  function photoPicker(maxFiles) {
    var tokens = [];
    var thumbs = el('div', { className: 'lmf-thumbs' });
    var input = el('input', {
      attrs: { type: 'file', accept: 'image/jpeg,image/png,image/webp', multiple: 'multiple' },
    });
    input.style.display = 'none';

    var drop = el('div', {
      className: 'lmf-drop',
      text: '📷 Add photos (up to ' + maxFiles + ', 5 MB each)',
      on: { click: function () { input.click(); } },
    });

    function renderThumbs() {
      clear(thumbs);
      tokens.forEach(function (entry, index) {
        if (entry.pending) {
          thumbs.appendChild(el('div', { className: 'lmf-thumb lmf-thumb--busy', text: '…' }));
          return;
        }
        thumbs.appendChild(
          el('div', { className: 'lmf-thumb' }, [
            el('img', { attrs: { src: entry.url, alt: '' } }),
            el('button', {
              text: '×',
              attrs: { type: 'button', 'aria-label': 'Remove photo' },
              on: {
                click: function () {
                  tokens.splice(index, 1);
                  renderThumbs();
                },
              },
            }),
          ]),
        );
      });
    }

    input.addEventListener('change', function () {
      var files = Array.prototype.slice.call(input.files || []);
      input.value = '';

      files.forEach(function (file) {
        if (tokens.length >= maxFiles) {
          showFlash('You can attach up to ' + maxFiles + ' photos.', 'err');
          return;
        }
        var slot = { pending: true };
        tokens.push(slot);
        renderThumbs();

        var form = new FormData();
        form.append('file', file);

        LMAuth.api('/api/photos', { method: 'POST', body: form })
          .then(function (payload) {
            slot.pending = false;
            slot.token = payload.token;
            slot.url = payload.photo.url;
            renderThumbs();
          })
          .catch(function (error) {
            var index = tokens.indexOf(slot);
            if (index > -1) tokens.splice(index, 1);
            renderThumbs();
            showFlash(error.message, 'err');
          });
      });
    });

    return {
      node: el('div', {}, [drop, thumbs, input]),
      tokens: function () {
        return tokens
          .filter(function (entry) {
            return !entry.pending && entry.token;
          })
          .map(function (entry) {
            return entry.token;
          });
      },
      busy: function () {
        return tokens.some(function (entry) {
          return entry.pending;
        });
      },
    };
  }

  // --- view: compose a thread --------------------------------------------

  function viewComposeThread(slug) {
    if (!LMAuth.me() || !LMAuth.me().canPost) {
      showFlash('Sign in to start a thread.', 'err');
      return go('#/b/' + slug);
    }

    clear(view);
    view.appendChild(
      crumbs([
        { label: 'Forum', hash: '#/' },
        { label: boardName(slug), hash: '#/b/' + slug },
        { label: 'New thread' },
      ]),
    );

    var title = el('input', { attrs: { type: 'text', maxlength: '140', id: 'lmf-new-title' } });
    var body = el('textarea', { attrs: { maxlength: '8000', id: 'lmf-new-body' } });
    var picker = photoPicker(3);

    var submit = el('button', {
      className: 'lmf-btn lmf-btn--primary',
      text: 'Post thread',
      attrs: { type: 'submit' },
    });

    var form = el(
      'form',
      {
        className: 'lmf-form',
        on: {
          submit: function (event) {
            event.preventDefault();
            if (picker.busy()) return showFlash('Wait for the photos to finish uploading.', 'err');

            submit.disabled = true;
            LMAuth.api('/api/threads', {
              method: 'POST',
              body: {
                boardSlug: slug,
                title: title.value,
                body: body.value,
                photos: picker.tokens(),
              },
            })
              .then(function (payload) {
                go('#/t/' + payload.thread.id);
              })
              .catch(function (error) {
                submit.disabled = false;
                showFlash(error.message, 'err');
              });
          },
        },
      },
      [
        el('h3', { text: 'Start a thread in ' + boardName(slug) }),
        el('div', { className: 'lmf-field' }, [
          el('label', { text: 'Title', attrs: { for: 'lmf-new-title' } }),
          title,
          el('div', {
            className: 'lmf-hint',
            text: 'Be specific — "Guppy fry losing colour after water change" beats "help".',
          }),
        ]),
        el('div', { className: 'lmf-field' }, [
          el('label', { text: 'Message', attrs: { for: 'lmf-new-body' } }),
          body,
          el('div', {
            className: 'lmf-hint',
            text: 'Tank size, water parameters and how long the issue has been going on all help people answer.',
          }),
        ]),
        el('div', { className: 'lmf-field' }, [picker.node]),
        el('div', { className: 'lmf-form-actions' }, [
          submit,
          el('button', {
            className: 'lmf-btn',
            text: 'Cancel',
            attrs: { type: 'button' },
            on: { click: function () { go('#/b/' + slug); } },
          }),
        ]),
      ],
    );

    view.appendChild(form);
    title.focus();
  }

  // --- reply composer -----------------------------------------------------

  function replyComposer(thread) {
    var body = el('textarea', { attrs: { maxlength: '8000', id: 'lmf-reply-body' } });
    var picker = photoPicker(3);
    var submit = el('button', {
      className: 'lmf-btn lmf-btn--primary',
      text: 'Post reply',
      attrs: { type: 'submit' },
    });

    return el(
      'form',
      {
        className: 'lmf-form',
        on: {
          submit: function (event) {
            event.preventDefault();
            if (picker.busy()) return showFlash('Wait for the photos to finish uploading.', 'err');

            submit.disabled = true;
            LMAuth.api('/api/threads/' + thread.id + '/replies', {
              method: 'POST',
              body: { body: body.value, photos: picker.tokens() },
            })
              .then(function () {
                viewThread(thread.id);
              })
              .catch(function (error) {
                submit.disabled = false;
                showFlash(error.message, 'err');
              });
          },
        },
      },
      [
        el('h3', { text: 'Reply' }),
        el('div', { className: 'lmf-field' }, [
          el('label', { text: 'Your reply', attrs: { for: 'lmf-reply-body' } }),
          body,
        ]),
        el('div', { className: 'lmf-field' }, [picker.node]),
        el('div', { className: 'lmf-form-actions' }, [submit]),
      ],
    );
  }

  // --- lightbox -----------------------------------------------------------

  function lightbox(url) {
    var overlay = el(
      'div',
      {
        className: 'lmf-lightbox',
        on: {
          click: function () {
            document.body.removeChild(overlay);
          },
        },
      },
      [el('img', { attrs: { src: url, alt: '' } })],
    );
    document.body.appendChild(overlay);
  }

  // --- start --------------------------------------------------------------

  window.addEventListener('hashchange', route);

  LMAuth.boot().then(function (result) {
    if (result.error) showFlash(result.error.message, 'err');
    else if (result.merged) {
      showFlash('Your store account and your Google sign-in are now one community profile.', 'ok');
    }
    renderAuthBar();
    route();
  });
})();
